import { queryOptions, useMutation } from '@tanstack/react-query';

import type { FileInputValue } from 'components/molecules/FileInput/types';
import config from 'config/index';
import { rumService } from 'services/rum';
import type { Base64String } from 'typings/file';

import { readChallengeHash } from './otp';

const verifyContentType = (response: Response) => {
  const contentType = response.headers.get('content-type');

  if (!contentType?.includes('application/json')) {
    throw new TypeError(
      `Unexpected Content-Type: ${contentType} in the response`,
    );
  }
};

/**
 * Retrieves a file from the server using its ID.
 *
 * @param params.fileId - The unique identifier of the file to retrieve.
 * @param params.businessEmail - The email whose challenge the hash belongs to.
 * @param params.emailValidationId - The hash of that challenge. Without it the BFF refuses the request.
 * @returns A `Base64String` if the file is successfully retrieved, or `null` if an error occurs.
 *
 * @throws This function does not throw errors directly but logs them using `rumService.addError`.
 *
 * @remarks
 * - The function sends a GET request to the server to fetch the file.
 * - If the response is not successful (`response.ok` is false), an error is logged, and `null` is returned.
 * - If an exception occurs during the fetch operation, it is caught, logged, and `null` is returned.
 *
 */

async function getFile({
  fileId,
  emailValidationId,
  businessEmail,
}: {
  fileId: string;
  emailValidationId: string | undefined;
  businessEmail: string;
}): Promise<Base64String | null> {
  if (!fileId) return null;

  const urlObj = new URL(
    `${config.PARTNERS_SELF_SIGNUP_BFF_URL}/v2/signup/file/${fileId}`,
  );
  urlObj.searchParams.set('businessEmail', businessEmail);
  if (emailValidationId) {
    urlObj.searchParams.set('emailValidationId', emailValidationId);
  }
  const url = urlObj.toString();

  try {
    const response = await fetch(url, {
      method: 'GET',
    });

    if (!response.ok) {
      rumService.addError(
        new Error(
          `[getFile] ${response.status} (${response.statusText}) - Unable to retrieve file with ID ${fileId} `,
        ),
      );
      return null;
    }

    const blob = await response.blob();

    // Convert to Base64 so TanStack Query can serialize it automatically
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as Base64String);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    rumService.addError(new Error(`[getFile] ${(error as Error).message} `));
    return null;
  }
}

function transformGetFileResultIntoFileFormValue({
  data,
  fileId,
}: {
  data: Base64String | null;
  fileId: string;
}): FileInputValue | null {
  if (data === null) {
    return null;
  }

  return { id: fileId, source: data };
}

/**
 * The BFF returns the bytes for the file id whatever the hash, so the key holds
 * the file id only. The hash is a credential: the query function reads the hash
 * of the `businessEmail` challenge when it runs.
 */
export function getFileQueryOptions({
  fileId,
  isEnabled = true,
  businessEmail,
}: {
  fileId: string;
  isEnabled?: boolean;
  /** The email whose challenge holds the hash the request sends. */
  businessEmail: string;
}) {
  return queryOptions({
    // The email only finds the hash. It never changes the bytes.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['get-file', fileId],
    // Returns null instead of disabling the query, see https://github.com/TanStack/query/discussions/6206
    queryFn: async ({ client }): Promise<Base64String | null> =>
      isEnabled
        ? getFile({
            fileId,
            businessEmail,
            emailValidationId: readChallengeHash({
              queryClient: client,
              email: businessEmail,
            }),
          })
        : null,
    select: (data: Base64String | null) =>
      transformGetFileResultIntoFileFormValue({ data, fileId }),
    refetchOnMount: false,
  });
}

type TUploadFileReturn = { id: string; success: boolean };

/**
 * Uploads a file to the server using the provided lead ID and file.
 *
 * @param variables -
 *  Object containing leadId (string) and the file (File) to upload.
 * @throws Will throw an error if the upload fails.
 * @returns A promise resolving to an object containing the uploaded file ID.
 */
export async function uploadFile({
  file,
  leadId,
  fileType = 'w9',
}: {
  leadId: string;
  file: File;
  fileType?: 'w9';
}): Promise<TUploadFileReturn> {
  const url = `${config.PARTNERS_SELF_SIGNUP_BFF_URL}/upload`;

  const formData = new FormData();
  formData.append('leadId', leadId);
  formData.append('file', file);
  formData.append('fileType', fileType);

  try {
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = new Error(
        `Upload failed: ${response.status} (${response.statusText})`,
      );

      throw error;
    }

    verifyContentType(response);
    const result = await response.json();
    return result;
  } catch (error) {
    rumService.addError(new Error(`[uploadFile] ${(error as Error).message}`));
    throw error;
  }
}

/**
 * Provides a React Query mutation hook for uploading files.
 *
 * @returns A mutation object with mutate functions and status properties.
 */
export function useUploadFile({
  onError,
}: {
  onError?: (error: Error) => void;
} = {}) {
  return useMutation({
    mutationFn: uploadFile,
    onError: error => {
      onError?.(error);
    },
  });
}
